import { Button, Input, Section } from '@patchbay/ui'
import { useQuery } from '@tanstack/react-query'
import * as React from 'react'
import { ApiError, describeError } from '../../lib/api/client'
import type { CliqSuggestionOut } from '../../lib/api/generated'
import { inboundCaseSearchQuery } from '../notifications/api'
import { unroutedCliqQuery, useDismissCliq, useFileCliq } from './api'

/** Cliq notes that did not name one live case. A person picks the case and files the internal note. */
export function CliqUnroutedInbox() {
  const query = useQuery(unroutedCliqQuery())
  if (query.isPending) return null
  if (query.isError) {
    const error = query.error
    if (error instanceof ApiError && (error.notBuilt || error.status === 404)) return null
    return <p className="text-sm text-muted-foreground">Cliq notes could not be loaded.</p>
  }
  const rows = query.data ?? []
  if (!rows.length) return null
  return (
    <Section title="Cliq notes to file" count={rows.length} bodyClassName="space-y-3">
      <p className="text-sm text-muted-foreground">
        Choose the case. The note goes on the internal pane and nothing is sent to the customer.
      </p>
      {rows.map((row) => (
        <CliqUnroutedCard key={row.id} row={row} />
      ))}
    </Section>
  )
}

export function CliqUnroutedCard({ row }: { row: CliqSuggestionOut }) {
  const [caseId, setCaseId] = React.useState<string | null>(null)
  const [caseLabel, setCaseLabel] = React.useState('')
  const [search, setSearch] = React.useState('')
  const searchQuery = useQuery(inboundCaseSearchQuery(search.trim()))
  const file = useFileCliq()
  const dismiss = useDismissCliq()
  const pending = file.isPending || dismiss.isPending
  const focused = new URLSearchParams(window.location.search).get('cliq') === row.id
  return (
    <article
      id={`cliq-${row.id}`}
      className={`space-y-3 rounded-lg border bg-card p-4 ${focused ? 'border-primary ring-1 ring-primary' : 'border-border'}`}
      data-testid="cliq-unrouted"
    >
      <div>
        <p className="font-medium">{row.kind === 'slash_command' ? 'Cliq note' : 'Cliq message'}</p>
        <p className="text-sm text-muted-foreground">
          {row.author_name || row.author_email || 'Someone'}
          {row.chat_title ? ` · ${row.chat_title}` : ''}
        </p>
      </div>
      {row.excerpt ? <p className="line-clamp-4 whitespace-pre-wrap text-sm">{row.excerpt}</p> : null}
      {row.named_cases.length ? (
        <p className="text-xs text-muted-foreground">Mentions {row.named_cases.join(', ')}</p>
      ) : (
        <p className="text-xs text-muted-foreground">No case number in the note.</p>
      )}
      <p className="text-sm text-muted-foreground">
        File this on the internal pane. Nothing is sent to the customer, and it does not go on the customer pane.
      </p>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
        <div className="relative min-w-0 flex-1">
          <Input
            aria-label="Find case"
            placeholder="Find case by C-number, customer, or subject"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          {search.trim() && searchQuery.data?.length ? (
            <div className="absolute z-10 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-border bg-popover p-1 shadow-lg">
              {searchQuery.data.map((hit) => (
                <button
                  key={hit.id}
                  type="button"
                  className="block w-full cursor-pointer rounded px-2 py-1.5 text-left text-sm hover:bg-muted"
                  onClick={() => {
                    setCaseId(hit.id)
                    setCaseLabel(hit.label)
                    setSearch('')
                  }}
                >
                  {hit.label}
                </button>
              ))}
            </div>
          ) : null}
          {caseId ? <p className="mt-1 truncate text-xs text-muted-foreground">{caseLabel}</p> : null}
        </div>
        <Button
          disabled={!caseId || dismiss.isPending}
          pending={file.isPending}
          onClick={() => caseId && file.mutate({ suggestionId: row.id, caseId })}
        >
          File on internal
        </Button>
        <Button variant="ghost" disabled={pending} onClick={() => dismiss.mutate(row.id)}>
          Dismiss
        </Button>
      </div>
      {searchQuery.isError || file.isError || dismiss.isError ? (
        <p className="text-sm text-warning">{describeError(searchQuery.error || file.error || dismiss.error)}</p>
      ) : null}
    </article>
  )
}
